import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Tabs,
  Tab,
  useTheme,
  Button,
  Chip,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Timeline } from "@mui/lab";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import WorkIcon from "@mui/icons-material/Work";
import GroupIcon from "@mui/icons-material/Group";

import SoftSkillCard from "./SoftSkillCard";
import ExperienceCard from "./ExperienceCard";

const WhoAmI = () => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);

  const softSkills = [
    { skill: "Teamwork", level: 3, color: theme.palette.primary.main },
    { skill: "Problem Solving", level: 4, color: theme.palette.success.main },
    { skill: "Time Management", level: 5, color: theme.palette.warning.main },
    { skill: "Creativity", level: 3, color: theme.palette.error.main },
    { skill: "Continuous Learning", level: 3, color: theme.palette.info.main },
    { skill: "Responsibility", level: 5, color: theme.palette.secondary.main },
  ];

  const experiences = [
    {
      year: "2024 - Present",
      title: "Teacher Assistant for M.E.R.N Courses at i3 Center",
      company: "I3-Center",
      description:
        "Supported student learning and provided guidance in Full-Stack Development, specializing in the MERN stack (MongoDB, Express.js, React, Node.js).",
    },
    {
      year: "2025 - Present",
      title: "Frontend Developer",
      company: "Vira Arvand Aria",
      description:
        "Build dynamic and performant front-end interfaces with React, ensuring pixel-perfect implementation of designs. Work cross-functionally with design and backend teams to deliver seamless user experiences.",
    },
  ];

  const chipData = [
    { label: "Gen-Z with a Vision", color: "primary" },
    { label: "Less Talk, More Build", color: "secondary" },
    { label: "Curious by Default", color: "info" },
  ];

  /* Mapping Variables */
  const softSkillItems = softSkills?.map((item, idx) => (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={idx}>
      <SoftSkillCard {...item} delay={idx * 0.1} />
    </Grid>
  ));

  const experienceItems = experiences?.map((exp, idx) => (
    <ExperienceCard key={idx} {...exp} delay={idx * 0.2} />
  ));

  const headerChips = chipData?.map((chip, idx) => (
    <motion.div
      key={idx}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
    >
      <Chip
        label={chip?.label}
        color={chip?.color}
        variant="outlined"
        sx={{
          borderWidth: 2,
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            transform: "translateY(-4px) ",
            backgroundColor: `${theme.palette[chip?.color]?.main}15`,
            boxShadow: `0 8px 20px -5px ${theme.palette[chip?.color]?.main}50`,
          },
          "&:active": {
            transform: "scale(0.95)",
          },
        }}
      />
    </motion.div>
  ));

  /* Animation Variants */
  const tabVariants = {
    initial: { opacity: 0, y: 15 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    exit: { opacity: 0, y: -15, transition: { duration: 0.2, ease: "easeIn" } },
  };

  return (
    <Container id="about-section" maxWidth="xl" sx={{ py: 8 }}>
      {/* Header Section */}
      <Box sx={{ textAlign: "center", mb: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {/* Subtle floating animation combined with hover effect */}
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            style={{ display: "inline-block" }}
          >
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <EmojiEmotionsIcon sx={{ fontSize: 80, color: "primary.main" }} />
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Typography variant="h5" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            Pouriya Sabouri, born in 2007, student at Azad University of
            Mashhad, Iran
          </Typography>
        </motion.div>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center",
            mt: 3,
            flexWrap: "wrap",
          }}
        >
          {headerChips}
        </Box>
      </Box>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 4 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, newIndex) => setTabIndex(newIndex)}
          centered
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Soft Skills" icon={<GroupIcon />} iconPosition="start" />
          <Tab label="Experience" icon={<WorkIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        {tabIndex === 0 && (
          <motion.div
            key="soft-skills"
            variants={tabVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Grid container spacing={3}>
              {softSkillItems}
            </Grid>
          </motion.div>
        )}

        {tabIndex === 1 && (
          <motion.div
            key="experience"
            variants={tabVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            <Timeline sx={{ p: 0 }} position="alternate">
              {experienceItems}
            </Timeline>
          </motion.div>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default WhoAmI;
